import { UserRoleName } from '../services/identity/types/UserProfile';

/**
 * Canonical base for the collector “create a case for a patient” flow:
 * patient list → profile → consultation type.
 * (Legacy `/collector/review-profile/...` redirects here.)
 */
export const COLLECTOR_CASE_CREATION_BASE_PATH = '/collector/case-creation';
export const CARE_ADVISOR_CASE_CREATION_BASE_PATH = '/careadvisor/case-creation';

const COLLECTOR_LEGACY_REVIEW_PROFILE_PREFIX = '/collector/review-profile';

/** Base path for staff-driven patient profile + case creation (admin vs collector). */
export function getStaffReviewProfileBasePath(roleName: string): string {
  if (roleName === UserRoleName.Collector) {
    return COLLECTOR_CASE_CREATION_BASE_PATH;
  }
  if (roleName === UserRoleName.CareAdvisor) {
    return CARE_ADVISOR_CASE_CREATION_BASE_PATH;
  }
  return `/${roleName}/review-profile`;
}

/** Resolve which staff flow base path the current URL belongs to (collector case-creation vs admin review-profile). */
export function getStaffPatientFlowBasePathFromLocation(pathname: string, roleName?: string): string {
  if (
    pathname.startsWith(`${COLLECTOR_CASE_CREATION_BASE_PATH}/`) ||
    pathname === COLLECTOR_CASE_CREATION_BASE_PATH ||
    pathname.startsWith(`${CARE_ADVISOR_CASE_CREATION_BASE_PATH}/`) ||
    pathname === CARE_ADVISOR_CASE_CREATION_BASE_PATH ||
    pathname.startsWith(`${COLLECTOR_LEGACY_REVIEW_PROFILE_PREFIX}/`) ||
    pathname === COLLECTOR_LEGACY_REVIEW_PROFILE_PREFIX
  ) {
    if (
      pathname.startsWith(`${CARE_ADVISOR_CASE_CREATION_BASE_PATH}/`) ||
      pathname === CARE_ADVISOR_CASE_CREATION_BASE_PATH
    ) {
      return CARE_ADVISOR_CASE_CREATION_BASE_PATH;
    }
    return COLLECTOR_CASE_CREATION_BASE_PATH;
  }
  // Extract the role prefix from the URL (e.g. /Admin/review-profile → /Admin) and reconstruct
  const segments = pathname.split('/').filter(Boolean);
  const urlRole = segments[0];
  if (urlRole) {
    return `/${urlRole}/review-profile`;
  }
  return roleName ? `/${roleName}/review-profile` : '/Admin/review-profile';
}

export function isStaffCaseCreationRole(roleName: string): boolean {
  return (
    roleName === UserRoleName.Admin ||
    roleName === UserRoleName.SuperAdmin ||
    roleName === UserRoleName.OrganizationAdmin ||
    roleName === UserRoleName.Collector ||
    roleName === UserRoleName.CareAdvisor
  );
}

/** End-user intake roles — must not use staff-behalf or unified staff intake branches. */
export function isEndUserPatientRole(roleName: string): boolean {
  return roleName === UserRoleName.Patient || roleName === UserRoleName.Dependent;
}

export function getPatientReviewProfileRoleSegment(
  profile: { role?: { name?: string } | string; guardian_id?: number | null } | null | undefined,
): UserRoleName.Patient | UserRoleName.Dependent {
  const roleName = typeof profile?.role === 'string' ? profile.role : profile?.role?.name;
  if (roleName === UserRoleName.Dependent || profile?.guardian_id != null) {
    return UserRoleName.Dependent;
  }
  return UserRoleName.Patient;
}

/**
 * Staff roles that share CreateConsultationFor unified intake routing (e.g. payment before provider / clinical / upload)
 * and the same intake UI (hidden sidenav, PatientIntakeNavigation) while a case is being created for a patient.
 * Matches when `staffCaseCreation.isStaffIntake` is set via `setStaffIntakeInProgress(true)`.
 */
export function isUnifiedStaffCaseIntakeRole(roleName: string): boolean {
  if (isEndUserPatientRole(roleName)) {
    return false;
  }
  return (
    roleName === UserRoleName.Collector ||
    roleName === UserRoleName.Admin ||
    roleName === UserRoleName.SuperAdmin ||
    roleName === UserRoleName.OrganizationAdmin ||
    roleName === UserRoleName.CareAdvisor
  );
}
